import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  GetPortalDocumentRoster,
  GetPortalDocumentRosterCount,
  PortalDocumentRoster,
  PortalDocumentRosterCount,
} from '../../services/dtos';

// D4/D5 staff roster (§5.11): the document's audience — every good-standing
// member of the local, or the assigned list for Specific documents — LEFT
// JOINed with their acknowledgment row. The header count polls the cheap
// count endpoint so staff can watch acks arrive live; "Refresh list" re-pulls
// the full roster when they want the rows too. The due date's only job is
// flagging who's outstanding (Form I: notify + track, never block).

const COUNT_POLL_MS = 5000;

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

// Due dates are date-only values stored at UTC midnight — format and compare
// them in UTC so the shown day never drifts by timezone.
function fmtDueDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString([], { dateStyle: 'medium', timeZone: 'UTC' });
}

// past due = the whole due day (UTC) has elapsed
function isPastDue(iso: string): boolean {
  const d = new Date(iso);
  return !isNaN(d.getTime()) && Date.now() >= d.getTime() + 24 * 3600 * 1000;
}

type Show = 'all' | 'acknowledged' | 'outstanding';

const DocumentRosterPage: React.FC = () => {
  const { id } = useParams();

  const [roster, setRoster] = useState<PortalDocumentRoster | null>(null);
  const [counts, setCounts] = useState<{ total: number; acknowledged: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState<Show>('all');

  const load = () =>
    apiServices.client
      .get(new GetPortalDocumentRoster({ id: Number(id) }))
      .then((r: PortalDocumentRoster) => {
        setRoster(r);
        setCounts({ total: r.total, acknowledged: r.acknowledged });
        setError(null);
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Loading the roster failed — try again.')
      );

  useEffect(() => {
    load();
  }, [id]);

  // the live count — two COUNT queries server-side, cheap enough to poll
  useEffect(() => {
    const t = setInterval(
      () =>
        apiServices.client
          .get(new GetPortalDocumentRosterCount({ id: Number(id) }))
          .then((c: PortalDocumentRosterCount) =>
            setCounts({ total: c.total, acknowledged: c.acknowledged })
          )
          .catch(() => undefined),
      COUNT_POLL_MS
    );
    return () => clearInterval(t);
  }, [id]);

  const rows = useMemo(() => {
    if (!roster) return [];
    const q = filter.trim().toLowerCase();
    return roster.rows.filter((r) => {
      if (show === 'acknowledged' && !r.acknowledgedAtUtc) return false;
      if (show === 'outstanding' && r.acknowledgedAtUtc) return false;
      if (q && !`${r.name} ${r.uniqueMemberId}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [roster, filter, show]);

  const showTypedNames =
    roster?.reviewMode === 'TypedName' ||
    (roster?.rows.some((r) => r.typedName) ?? false);

  return (
    <div className="mx-auto w-full max-w-4xl flex flex-col gap-4 dark:text-white">
      <div>
        <Link
          to="/doc-admin"
          className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          ← Document Admin
        </Link>
        {roster && (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-xl font-bold break-words">{roster.title}</h2>
            <span className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              {roster.reviewMode === 'TypedName' ? 'Typed name' : 'Acknowledge'}
            </span>
            {roster.audience === 'Specific' && (
              <span className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                Specific audience
              </span>
            )}
            {roster.dueAtUtc && (
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  isPastDue(roster.dueAtUtc)
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                }`}
              >
                Due {fmtDueDate(roster.dueAtUtc)}
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <Toast color="failure">
          <div className="flex items-center gap-3">
            <span>{error}</span>
            <button onClick={load} className="font-medium underline">
              Reload
            </button>
          </div>
        </Toast>
      )}

      {!roster && !error && <Spinner color="info" />}

      {roster && counts && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div>
            <span className="text-2xl font-bold">{counts.acknowledged}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {' '}
              of {counts.total}{' '}
              {roster.audience === 'Specific'
                ? 'assigned members'
                : 'good-standing members'}{' '}
              acknowledged
            </span>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Count updates live
          </span>
          <button
            onClick={load}
            className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Refresh list
          </button>
        </div>
      )}

      {roster && (
        <div className="flex flex-wrap items-center gap-3">
          <input
            id="roster-search"
            name="rosterSearch"
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search name or member #"
            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
          />
          <div className="flex gap-1 text-xs font-medium">
            {(['all', 'acknowledged', 'outstanding'] as Show[]).map((s) => (
              <button
                key={s}
                onClick={() => setShow(s)}
                className={`rounded px-2 py-1 ${
                  show === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                {s === 'all'
                  ? `All (${roster.total})`
                  : s === 'acknowledged'
                  ? `Acknowledged (${roster.acknowledged})`
                  : `Outstanding (${roster.total - roster.acknowledged})`}
              </button>
            ))}
          </div>
        </div>
      )}

      {roster && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-3 py-2">Member</th>
                <th className="px-3 py-2 w-40">Member #</th>
                <th className="px-3 py-2 w-48">Acknowledged</th>
                {showTypedNames && <th className="px-3 py-2 w-48">Signed as</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {rows.map((r) => (
                <tr key={r.uniqueMemberId}>
                  <td className="px-3 py-1.5 text-gray-900 dark:text-white">
                    {r.name}
                  </td>
                  <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                    {r.uniqueMemberId}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.acknowledgedAtUtc ? (
                      r.priorVersion ? (
                        // §5.11 evidence binding: the ack's hash snapshot no
                        // longer matches this document's PDF — never show a
                        // bare check mark for an ack of different bytes
                        <span
                          className="text-amber-700 dark:text-amber-400"
                          title="This member acknowledged an earlier file for this document"
                        >
                          {fmtWhen(r.acknowledgedAtUtc)} — prior version
                        </span>
                      ) : (
                        <span className="text-green-700 dark:text-green-400">
                          {fmtWhen(r.acknowledgedAtUtc)}
                        </span>
                      )
                    ) : roster.dueAtUtc && isPastDue(roster.dueAtUtc) ? (
                      // Form I: the due date's job is flagging who's
                      // outstanding — it never blocks anyone
                      <span className="font-medium text-red-600 dark:text-red-400">
                        outstanding — overdue
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  {showTypedNames && (
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                      {r.typedName || ''}
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={showTypedNames ? 4 : 3}
                    className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    No members match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentRosterPage;
